import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone } from 'lucide-react';
import ChatBot from './ChatBot';
import { trackCallClick, trackWhatsAppClick, trackChatOpen } from '../lib/tracker';

/**
 * Global floating contact bar.
 * 4 small icons in a horizontal pill — Phone | WhatsApp | AiSensy WA | Chatbot
 * Only visible on homepage hero area. Hides on scroll or other routes.
 */
import { WA_URL, CALL_TEL } from '../lib/contact';

const SCROLL_HIDE_PX = 180;

export default function FloatingContact({ onOpenCalculator }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(true);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const t = setTimeout(() => setMounted(true), 250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { setChatOpen(false); }, [pathname]);

  useEffect(() => {
    if (!chatOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setChatOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [chatOpen]);

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY < SCROLL_HIDE_PX);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // AiSensy trigger
  const openAiSensy = useCallback(() => {
    const btn = document.querySelector('#df-btn-cont .df-btn') || document.querySelector('[class*="df-btn"]');
    if (btn) {
      const cont = document.querySelector('#df-btn-cont');
      if (cont) { cont.style.display = 'block'; cont.style.visibility = 'hidden'; }
      btn.click();
      setTimeout(() => { if (cont) { cont.style.display = 'none'; cont.style.visibility = ''; } }, 200);
    } else {
      window.open(WA_URL, '_blank');
    }
  }, []);

  const openChat = () => {
    setChatOpen(true);
    setShowBadge(false);
    trackChatOpen({ source: 'floating_contact' });
  };
  const closeChat = () => setChatOpen(false);

  if (pathname.startsWith('/admin') || pathname.startsWith('/official')) return (
    <>
      <ChatBot open={chatOpen} onClose={closeChat} onOpenCalculator={onOpenCalculator} />
    </>
  );

  const isHome = pathname === '/' || pathname === '';
  const showBar = mounted && visible && isHome;

  return (
    <>
      {/* ======== UNIFIED HORIZONTAL BAR — all screen sizes ======== */}
      <div
        data-testid="floating-contact-bar"
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 transition-all duration-400 ${
          showBar
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-8 pointer-events-none'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center gap-2.5 sm:gap-3 bg-white/90 backdrop-blur-md rounded-full px-3 py-2 shadow-[0_8px_32px_rgba(0,0,0,0.18)] ring-1 ring-black/5">
          {/* 1. Phone */}
          <a
            href={CALL_TEL}
            onClick={() => trackCallClick({ position: 'floating_bar' })}
            data-testid="float-call-btn"
            aria-label="Call expert"
            className="active:scale-90 transition-transform"
          >
            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0a2540] text-white inline-flex items-center justify-center shadow-md">
              <Phone size={16} strokeWidth={2.2} />
            </span>
          </a>

          {/* 2. WhatsApp (direct) */}
          <a
            href={WA_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWhatsAppClick({ position: 'floating_bar' })}
            data-testid="float-wa-btn"
            aria-label="WhatsApp EntoRid"
            className="active:scale-90 transition-transform"
          >
            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#25D366] text-white inline-flex items-center justify-center shadow-md">
              <WhatsAppIcon size={17} />
            </span>
          </a>

          {/* 3. AiSensy WhatsApp Business */}
          <button
            type="button"
            onClick={openAiSensy}
            data-testid="float-aisensy-btn"
            aria-label="WhatsApp Business Chat"
            className="active:scale-90 transition-transform"
          >
            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0a2540] text-white inline-flex items-center justify-center shadow-md">
              <WhatsAppIcon size={17} />
            </span>
          </button>

          {/* 4. Chatbot */}
          <button
            type="button"
            onClick={openChat}
            data-testid="float-chat-btn"
            aria-label="Chat with pest expert"
            className="relative active:scale-90 transition-transform"
          >
            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-brand-green to-emerald-700 inline-flex items-center justify-center shadow-md overflow-hidden">
              <TechnicianAvatar size={32} />
            </span>
            {showBadge && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[7px] font-bold flex items-center justify-center ring-[1.5px] ring-white">
                1
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ======== CHATBOT PANEL ======== */}
      <ChatBot open={chatOpen} onClose={closeChat} onOpenCalculator={onOpenCalculator} />
    </>
  );
}

// ---------- SVG Helpers ----------
function WhatsAppIcon({ size = 26 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 0 5.37 0 12a11.94 11.94 0 001.64 6l-1.72 6.3 6.47-1.7A11.94 11.94 0 0012 24c6.63 0 12-5.37 12-12a11.94 11.94 0 00-3.48-8.52zM12 21.82a9.83 9.83 0 01-5-1.36l-.36-.21-3.85 1.02 1.03-3.76-.23-.38A9.83 9.83 0 012.18 12 9.82 9.82 0 0112 2.18 9.82 9.82 0 0121.82 12 9.83 9.83 0 0112 21.82zm5.41-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.6.14-.13.3-.34.45-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.21 5.08 4.5.71.31 1.26.49 1.69.63.71.22 1.35.19 1.86.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"/>
    </svg>
  );
}

function TechnicianAvatar({ size = 54 }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden className="drop-shadow-sm">
      <defs>
        <linearGradient id="techSky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#dff7ec" /><stop offset="100%" stopColor="#a7e9c5" /></linearGradient>
        <linearGradient id="techHat" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16a34a" /><stop offset="100%" stopColor="#0d7a37" /></linearGradient>
        <linearGradient id="techShirt" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e3a8a" /><stop offset="100%" stopColor="#0a2540" /></linearGradient>
      </defs>
      <circle cx="32" cy="32" r="32" fill="url(#techSky)" />
      <path d="M8 60 C 14 48, 22 44, 32 44 C 42 44, 50 48, 56 60 Z" fill="url(#techShirt)" />
      <path d="M28 47 L32 53 L36 47 Z" fill="#dff7ec" opacity="0.85" />
      <rect x="28" y="38" width="8" height="7" rx="2" fill="#f1c8a5" />
      <ellipse cx="32" cy="30" rx="11" ry="12" fill="#fadcb8" />
      <ellipse cx="21" cy="31" rx="2" ry="3" fill="#f1c8a5" />
      <ellipse cx="43" cy="31" rx="2" ry="3" fill="#f1c8a5" />
      <path d="M21 27 Q 22 33 24 36 L 26 36 L 26 27 Z" fill="#2a1e10" />
      <path d="M43 27 Q 42 33 40 36 L 38 36 L 38 27 Z" fill="#2a1e10" />
      <rect x="25" y="26" width="4" height="1.2" rx="0.6" fill="#2a1e10" />
      <rect x="35" y="26" width="4" height="1.2" rx="0.6" fill="#2a1e10" />
      <circle cx="27" cy="29" r="1.2" fill="#1e293b" />
      <circle cx="37" cy="29" r="1.2" fill="#1e293b" />
      <circle cx="27.4" cy="28.6" r="0.4" fill="#fff" />
      <circle cx="37.4" cy="28.6" r="0.4" fill="#fff" />
      <path d="M32 30 L 31 34 L 33 34 Z" fill="#e1a87a" />
      <path d="M27 36 Q 32 40 37 36" stroke="#7c2d12" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <path d="M28 37 Q 32 39 36 37" stroke="#fff" strokeWidth="0.6" fill="none" opacity="0.7" />
      <path d="M18 22 Q 32 8 46 22 L 46 24 L 18 24 Z" fill="url(#techHat)" />
      <rect x="16" y="23.5" width="32" height="3" rx="1.5" fill="#0a5a28" />
      <circle cx="32" cy="18" r="3.6" fill="#fff" />
      <path d="M32 15.4 q 2.2 1 2 3.6 q -2 0.2 -3.4 -1 q 0.2 1.6 1.6 2.2 q -2 0.4 -3 -1.6 q -0.4 -1.6 0.8 -3 q 1 -1 2 -0.2 Z" fill="#16a34a" />
    </svg>
  );
}
